import { useState, useEffect } from 'react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function Disciplinary({ user }) {
  const [activeTab, setActiveTab] = useState('memos');
  const [counselingMemos, setCounselingMemos] = useState([]);
  const [enforcementActions, setEnforcementActions] = useState([]);
  const [ethicsViolations, setEthicsViolations] = useState([]);
  const [appeals, setAppeals] = useState([]);
  const [investigations, setInvestigations] = useState([]);
  const [notifications, setNotifications] = useState([]);
  const [personnel, setPersonnel] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showMemoModal, setShowMemoModal] = useState(false);
  const [showEnforcementModal, setShowEnforcementModal] = useState(false);
  const [showEthicsModal, setShowEthicsModal] = useState(false);
  const [showAppealModal, setShowAppealModal] = useState(false);
  const [showDecideAppealModal, setShowDecideAppealModal] = useState(false);
  const [showAssignInvestigationModal, setShowAssignInvestigationModal] = useState(false);
  const [selectedAppeal, setSelectedAppeal] = useState(null);
  const [selectedViolation, setSelectedViolation] = useState(null);
  const [selectedAction, setSelectedAction] = useState(null);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const [memoForm, setMemoForm] = useState({
    user_id: '',
    incident_date: new Date().toISOString().split('T')[0],
    category: 'Conduct',
    severity: 'Verbal',
    description: '',
    expectations: '',
    consequences: '',
  });

  const [enforcementForm, setEnforcementForm] = useState({
    user_id: '',
    action_type: 'Warning',
    effective_date: new Date().toISOString().split('T')[0],
    end_date: '',
    reason: '',
    terms_and_conditions: '',
    appeal_deadline: '',
  });

  const [ethicsForm, setEthicsForm] = useState({
    user_id: '',
    incident_date: new Date().toISOString().split('T')[0],
    violation_type: 'Policy Violation',
    severity: 'Minor',
    description: '',
  });

  const [appealForm, setAppealForm] = useState({
    enforcement_action_id: '',
    grounds_for_appeal: '',
    supporting_statement: '',
  });

  const [decisionForm, setDecisionForm] = useState({
    decision: 'Upheld',
    decision_rationale: '',
    modified_terms: '',
  });

  const [investigationForm, setInvestigationForm] = useState({
    investigator_id: '',
    investigation_scope: '',
    deadline: '',
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [memosRes, actionsRes, violationsRes, appealsRes, investigationsRes, notificationsRes, personnelRes] = await Promise.all([
        axios.get(`${API}/disciplinary/counseling-memos`),
        axios.get(`${API}/disciplinary/enforcement-actions`),
        axios.get(`${API}/disciplinary/ethics-violations`),
        axios.get(`${API}/disciplinary/appeals`),
        axios.get(`${API}/disciplinary/investigations`),
        axios.get(`${API}/disciplinary/notifications`),
        axios.get(`${API}/personnel`),
      ]);
      setCounselingMemos(memosRes.data);
      setEnforcementActions(actionsRes.data);
      setEthicsViolations(violationsRes.data);
      setAppeals(appealsRes.data);
      setInvestigations(investigationsRes.data);
      setNotifications(notificationsRes.data);
      setPersonnel(personnelRes.data);
    } catch (error) {
      console.error('Failed to fetch disciplinary data:', error);
      setError(error.response?.data?.detail || 'Failed to load disciplinary data');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateMemo = async (e) => {
    e.preventDefault();
    setError('');
    try {
      await axios.post(`${API}/disciplinary/counseling-memo`, memoForm);
      setSuccess('Counseling memo created successfully!');
      setShowMemoModal(false);
      setMemoForm({
        user_id: '',
        incident_date: new Date().toISOString().split('T')[0],
        category: 'Conduct',
        severity: 'Verbal',
        description: '',
        expectations: '',
        consequences: '',
      });
      fetchData();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to create counseling memo');
    }
  };

  const handleCreateEnforcement = async (e) => {
    e.preventDefault();
    setError('');
    try {
      const data = { ...enforcementForm };
      if (!data.end_date) delete data.end_date;
      if (!data.appeal_deadline) {
        data.appeal_deadline = new Date(Date.now() + 14 * 24 * 60 * 60 * 1000).toISOString().split('T')[0];
      }
      await axios.post(`${API}/disciplinary/enforcement-action`, data);
      setSuccess('Enforcement action created successfully!');
      setShowEnforcementModal(false);
      setEnforcementForm({
        user_id: '',
        action_type: 'Warning',
        effective_date: new Date().toISOString().split('T')[0],
        end_date: '',
        reason: '',
        terms_and_conditions: '',
        appeal_deadline: '',
      });
      fetchData();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to create enforcement action');
    }
  };

  const handleReportEthics = async (e) => {
    e.preventDefault();
    setError('');
    try {
      await axios.post(`${API}/disciplinary/ethics-violation`, ethicsForm);
      setSuccess('Ethics violation reported successfully!');
      setShowEthicsModal(false);
      setEthicsForm({
        user_id: '',
        incident_date: new Date().toISOString().split('T')[0],
        violation_type: 'Policy Violation',
        severity: 'Minor',
        description: '',
      });
      fetchData();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to report ethics violation');
    }
  };

  const handleFileAppeal = async (e) => {
    e.preventDefault();
    setError('');
    try {
      await axios.post(`${API}/disciplinary/appeal`, {
        ...appealForm,
        enforcement_action_id: selectedAction.id,
      });
      setSuccess('Appeal filed successfully!');
      setShowAppealModal(false);
      setAppealForm({ enforcement_action_id: '', grounds_for_appeal: '', supporting_statement: '' });
      setSelectedAction(null);
      fetchData();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to file appeal');
    }
  };

  const handleDecideAppeal = async (e) => {
    e.preventDefault();
    setError('');
    try {
      await axios.put(`${API}/disciplinary/appeal/${selectedAppeal.id}/decide`, decisionForm);
      setSuccess(`Appeal ${decisionForm.decision.toLowerCase()} successfully!`);
      setShowDecideAppealModal(false);
      setDecisionForm({ decision: 'Upheld', decision_rationale: '', modified_terms: '' });
      setSelectedAppeal(null);
      fetchData();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to decide appeal');
    }
  };

  const handleAssignInvestigation = async (e) => {
    e.preventDefault();
    setError('');
    try {
      await axios.post(`${API}/disciplinary/ethics-violation/${selectedViolation.id}/assign-investigation`, investigationForm);
      setSuccess('Investigation assigned successfully!');
      setShowAssignInvestigationModal(false);
      setInvestigationForm({ investigator_id: '', investigation_scope: '', deadline: '' });
      setSelectedViolation(null);
      fetchData();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to assign investigation');
    }
  };

  const getSeverityBadge = (severity) => {
    const styles = {
      'Verbal': 'bg-slate-900/50 text-slate-300 border-slate-700',
      'Written': 'bg-amber-900/50 text-amber-300 border-amber-700',
      'Final Written': 'bg-orange-900/50 text-orange-300 border-orange-700',
      'Minor': 'bg-emerald-900/50 text-emerald-300 border-emerald-700',
      'Major': 'bg-amber-900/50 text-amber-300 border-amber-700',
      'Critical': 'bg-red-900/50 text-red-300 border-red-700',
    };
    return styles[severity] || 'bg-slate-900/50 text-slate-300 border-slate-700';
  };

  const getStatusBadge = (status) => {
    const styles = {
      'Active': 'bg-amber-900/50 text-amber-300 border-amber-700',
      'Resolved': 'bg-emerald-900/50 text-emerald-300 border-emerald-700',
      'Escalated': 'bg-red-900/50 text-red-300 border-red-700',
      'Reported': 'bg-blue-900/50 text-blue-300 border-blue-700',
      'Under Investigation': 'bg-purple-900/50 text-purple-300 border-purple-700',
      'Substantiated': 'bg-red-900/50 text-red-300 border-red-700',
      'Unsubstantiated': 'bg-slate-900/50 text-slate-300 border-slate-700',
      'Closed': 'bg-emerald-900/50 text-emerald-300 border-emerald-700',
      'Pending': 'bg-amber-900/50 text-amber-300 border-amber-700',
      'Decided': 'bg-blue-900/50 text-blue-300 border-blue-700',
      'Upheld': 'bg-slate-900/50 text-slate-300 border-slate-700',
      'Modified': 'bg-amber-900/50 text-amber-300 border-amber-700',
      'Overturned': 'bg-emerald-900/50 text-emerald-300 border-emerald-700',
    };
    return styles[status] || 'bg-slate-900/50 text-slate-300 border-slate-700';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="spinner"></div>
      </div>
    );
  }

  const unreadNotifications = notifications.filter(n => !n.read);

  return (
    <div className="space-y-6 animate-fade-in" data-testid="disciplinary-page">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-white mb-2">Disciplinary & Ethics</h1>
          <p className="text-slate-400">Counseling, enforcement, ethics violations, appeals & investigations</p>
        </div>
        <div className="flex gap-3">
          {user.rank?.level >= 2 && (
            <button
              onClick={() => setShowMemoModal(true)}
              className="btn-military flex items-center gap-2"
              data-testid="create-memo-button"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
              Memo
            </button>
          )}
          {user.rank?.level >= 5 && (
            <button
              onClick={() => setShowEnforcementModal(true)}
              className="btn-military flex items-center gap-2"
              data-testid="enforcement-action-button"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
              Enforcement
            </button>
          )}
          <button
            onClick={() => setShowEthicsModal(true)}
            className="btn-gold flex items-center gap-2"
            data-testid="report-ethics-button"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
            </svg>
            Report Ethics
          </button>
        </div>
      </div>

      {/* Alerts */}
      {error && (
        <div className="bg-red-900/30 border border-red-700 text-red-300 px-4 py-3 rounded-lg">
          {error}
        </div>
      )}
      {success && (
        <div className="bg-emerald-900/30 border border-emerald-700 text-emerald-300 px-4 py-3 rounded-lg">
          {success}
        </div>
      )}

      {/* Notifications Banner */}
      {unreadNotifications.length > 0 && (
        <div className="bg-amber-900/30 border border-amber-700 rounded-lg p-4">
          <div className="flex items-center gap-2 text-amber-300 font-medium mb-2">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
            </svg>
            {unreadNotifications.length} Unread Notifications
          </div>
          <div className="space-y-2">
            {unreadNotifications.slice(0, 3).map((notif) => (
              <div key={notif.id} className="text-sm text-slate-300">
                • {notif.message}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-6 gap-4">
        <div className="military-card p-4">
          <p className="text-slate-400 text-xs">Memos</p>
          <p className="text-2xl font-bold text-white">{counselingMemos.length}</p>
        </div>
        <div className="military-card p-4">
          <p className="text-slate-400 text-xs">Enforcement</p>
          <p className="text-2xl font-bold text-red-400">{enforcementActions.length}</p>
        </div>
        <div className="military-card p-4">
          <p className="text-slate-400 text-xs">Ethics</p>
          <p className="text-2xl font-bold text-purple-400">{ethicsViolations.length}</p>
        </div>
        <div className="military-card p-4">
          <p className="text-slate-400 text-xs">Appeals</p>
          <p className="text-2xl font-bold text-amber-400">{appeals.filter(a => a.status === 'Pending').length}</p>
        </div>
        <div className="military-card p-4">
          <p className="text-slate-400 text-xs">Investigations</p>
          <p className="text-2xl font-bold text-blue-400">{investigations.length}</p>
        </div>
        <div className="military-card p-4">
          <p className="text-slate-400 text-xs">Notifications</p>
          <p className="text-2xl font-bold text-emerald-400">{unreadNotifications.length}</p>
        </div>
      </div>

      {/* Tab Navigation */}
      <div className="tab-nav flex-wrap">
        <button onClick={() => setActiveTab('memos')} className={`tab-button ${activeTab === 'memos' ? 'active' : ''}`}>
          Memos ({counselingMemos.length})
        </button>
        <button onClick={() => setActiveTab('enforcement')} className={`tab-button ${activeTab === 'enforcement' ? 'active' : ''}`}>
          Enforcement ({enforcementActions.length})
        </button>
        <button onClick={() => setActiveTab('ethics')} className={`tab-button ${activeTab === 'ethics' ? 'active' : ''}`}>
          Ethics ({ethicsViolations.length})
        </button>
        <button onClick={() => setActiveTab('appeals')} className={`tab-button ${activeTab === 'appeals' ? 'active' : ''}`}>
          Appeals ({appeals.length})
        </button>
        <button onClick={() => setActiveTab('investigations')} className={`tab-button ${activeTab === 'investigations' ? 'active' : ''}`}>
          Investigations ({investigations.length})
        </button>
      </div>

      {/* Counseling Memos Tab */}
      {activeTab === 'memos' && (
        <div className="space-y-4">
          {counselingMemos.length === 0 ? (
            <div className="text-center py-12 text-slate-500 military-card">No counseling memos found</div>
          ) : (
            counselingMemos.map((memo) => {
              const person = personnel.find(p => p.id === memo.user_id);
              return (
                <div key={memo.id} className="military-card p-5" data-testid={`memo-${memo.memo_number}`}>
                  <div className="flex justify-between items-start mb-3">
                    <div>
                      <div className="flex items-center gap-3 mb-1">
                        <span className="text-amber-400 font-mono text-sm">{memo.memo_number}</span>
                        <span className={`px-2 py-1 text-xs font-medium rounded border ${getSeverityBadge(memo.severity)}`}>{memo.severity}</span>
                        <span className="px-2 py-1 text-xs font-medium rounded bg-slate-800 text-slate-300">{memo.category}</span>
                      </div>
                      <p className="text-white font-medium">{person?.full_name || 'Unknown'}</p>
                    </div>
                    <span className={`px-2 py-1 text-xs font-medium rounded border ${getStatusBadge(memo.status)}`}>{memo.status}</span>
                  </div>
                  <p className="text-slate-400 text-sm mb-3">{memo.description}</p>
                  <div className="grid grid-cols-2 gap-4 pt-3 border-t border-slate-700 text-sm">
                    <div><span className="text-slate-500">Expectations:</span><p className="text-slate-300">{memo.expectations}</p></div>
                    <div><span className="text-slate-500">Consequences:</span><p className="text-slate-300">{memo.consequences}</p></div>
                  </div>
                  <p className="text-slate-500 text-xs mt-3">Incident Date: {memo.incident_date}</p>
                </div>
              );
            })
          )}
        </div>
      )}

      {/* Enforcement Actions Tab */}
      {activeTab === 'enforcement' && (
        <div className="space-y-4">
          {enforcementActions.length === 0 ? (
            <div className="text-center py-12 text-slate-500 military-card">No enforcement actions found</div>
          ) : (
            enforcementActions.map((action) => {
              const person = personnel.find(p => p.id === action.user_id);
              const canAppeal = action.user_id === user.id && action.status === 'Active' && !action.appeal_status;
              return (
                <div key={action.id} className="military-card p-5 border-l-4 border-red-500" data-testid={`action-${action.action_number}`}>
                  <div className="flex justify-between items-start mb-3">
                    <div>
                      <div className="flex items-center gap-3 mb-1">
                        <span className="text-red-400 font-mono text-sm">{action.action_number}</span>
                        <span className="px-2 py-1 text-xs font-semibold rounded bg-red-900/50 text-red-300 border border-red-700">{action.action_type}</span>
                        {action.appeal_status && (
                          <span className={`px-2 py-1 text-xs font-medium rounded border ${getStatusBadge(action.appeal_status)}`}>
                            Appeal: {action.appeal_status}
                          </span>
                        )}
                      </div>
                      <p className="text-white font-medium">{person?.full_name || action.subject_name || 'Unknown'}</p>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className={`px-2 py-1 text-xs font-medium rounded border ${getStatusBadge(action.status)}`}>{action.status}</span>
                      {canAppeal && (
                        <button
                          onClick={() => { setSelectedAction(action); setShowAppealModal(true); }}
                          className="text-amber-400 hover:text-amber-300 text-xs font-medium"
                        >
                          File Appeal
                        </button>
                      )}
                    </div>
                  </div>
                  <p className="text-slate-400 text-sm mb-3">{action.reason}</p>
                  <div className="flex items-center gap-4 text-sm text-slate-500">
                    <span>Effective: {action.effective_date}</span>
                    {action.end_date && <><span>•</span><span>End: {action.end_date}</span></>}
                    {action.appeal_deadline && <><span>•</span><span>Appeal by: {action.appeal_deadline}</span></>}
                  </div>
                </div>
              );
            })
          )}
        </div>
      )}

      {/* Ethics Violations Tab */}
      {activeTab === 'ethics' && (
        <div className="space-y-4">
          {ethicsViolations.length === 0 ? (
            <div className="text-center py-12 text-slate-500 military-card">No ethics violations reported</div>
          ) : (
            ethicsViolations.map((violation) => {
              const person = personnel.find(p => p.id === violation.user_id);
              const canAssignInvestigation = user.rank?.level === 5 && 
                (!violation.investigation_status || violation.investigation_status === 'Reported');
              return (
                <div key={violation.id} className="military-card p-5" data-testid={`violation-${violation.violation_number}`}>
                  <div className="flex justify-between items-start mb-3">
                    <div>
                      <div className="flex items-center gap-3 mb-1">
                        <span className="text-purple-400 font-mono text-sm">{violation.violation_number}</span>
                        <span className={`px-2 py-1 text-xs font-medium rounded border ${getSeverityBadge(violation.severity)}`}>{violation.severity}</span>
                        <span className="px-2 py-1 text-xs font-medium rounded bg-purple-900/50 text-purple-300">{violation.violation_type}</span>
                        {violation.confidential && (
                          <span className="px-2 py-1 text-xs font-medium rounded bg-slate-800 text-slate-400">CONFIDENTIAL</span>
                        )}
                      </div>
                      <p className="text-white font-medium">{person?.full_name || 'Unknown'}</p>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className={`px-2 py-1 text-xs font-medium rounded border ${getStatusBadge(violation.investigation_status)}`}>
                        {violation.investigation_status}
                      </span>
                      {canAssignInvestigation && (
                        <button
                          onClick={() => { setSelectedViolation(violation); setShowAssignInvestigationModal(true); }}
                          className="text-blue-400 hover:text-blue-300 text-xs font-medium"
                        >
                          Assign Investigation
                        </button>
                      )}
                    </div>
                  </div>
                  <p className="text-slate-400 text-sm mb-3">{violation.description}</p>
                  <p className="text-slate-500 text-xs">Incident Date: {violation.incident_date}</p>
                </div>
              );
            })
          )}
        </div>
      )}

      {/* Appeals Tab */}
      {activeTab === 'appeals' && (
        <div className="space-y-4">
          {appeals.length === 0 ? (
            <div className="text-center py-12 text-slate-500 military-card">No appeals filed</div>
          ) : (
            appeals.map((appeal) => (
              <div key={appeal.id} className="military-card p-5" data-testid={`appeal-${appeal.id}`}>
                <div className="flex justify-between items-start mb-3">
                  <div>
                    <div className="flex items-center gap-3 mb-1">
                      <span className="text-amber-400 font-mono text-sm">Appeal: {appeal.action_number}</span>
                      <span className={`px-2 py-1 text-xs font-medium rounded border ${getStatusBadge(appeal.status)}`}>
                        {appeal.status}
                      </span>
                      {appeal.decision && (
                        <span className={`px-2 py-1 text-xs font-medium rounded border ${getStatusBadge(appeal.decision)}`}>
                          {appeal.decision}
                        </span>
                      )}
                    </div>
                    <p className="text-white font-medium">Filed by: {appeal.appellant_name}</p>
                  </div>
                  {appeal.status === 'Pending' && user.rank?.level === 5 && (
                    <button
                      onClick={() => { setSelectedAppeal(appeal); setShowDecideAppealModal(true); }}
                      className="btn-gold text-sm py-1 px-3"
                    >
                      Decide
                    </button>
                  )}
                </div>
                <div className="space-y-2 text-sm">
                  <div><span className="text-slate-500">Grounds:</span> <span className="text-slate-300">{appeal.grounds_for_appeal}</span></div>
                  <div><span className="text-slate-500">Statement:</span> <span className="text-slate-300">{appeal.supporting_statement}</span></div>
                  {appeal.decision_rationale && (
                    <div className="pt-2 mt-2 border-t border-slate-700">
                      <span className="text-slate-500">Decision Rationale:</span>
                      <span className="text-slate-300 ml-2">{appeal.decision_rationale}</span>
                    </div>
                  )}
                </div>
                <div className="flex items-center gap-4 text-xs text-slate-500 mt-3">
                  <span>Filed: {new Date(appeal.filed_date).toLocaleDateString()}</span>
                  <span>•</span>
                  <span>Review Deadline: {appeal.review_deadline}</span>
                </div>
              </div>
            ))
          )}
        </div>
      )}

      {/* Investigations Tab */}
      {activeTab === 'investigations' && (
        <div className="space-y-4">
          {investigations.length === 0 ? (
            <div className="text-center py-12 text-slate-500 military-card">No investigations assigned</div>
          ) : (
            investigations.map((inv) => (
              <div key={inv.id} className="military-card p-5" data-testid={`investigation-${inv.id}`}>
                <div className="flex justify-between items-start mb-3">
                  <div>
                    <div className="flex items-center gap-3 mb-1">
                      <span className="text-blue-400 font-mono text-sm">{inv.violation_number}</span>
                      <span className={`px-2 py-1 text-xs font-medium rounded border ${getStatusBadge(inv.status)}`}>{inv.status}</span>
                    </div>
                    <p className="text-white font-medium">Investigator: {inv.investigator_name}</p>
                  </div>
                </div>
                <div className="space-y-2 text-sm">
                  <div><span className="text-slate-500">Scope:</span> <span className="text-slate-300">{inv.investigation_scope}</span></div>
                  {inv.findings && (
                    <div><span className="text-slate-500">Findings:</span> <span className="text-slate-300">{inv.findings}</span></div>
                  )}
                  {inv.recommendations && (
                    <div><span className="text-slate-500">Recommendations:</span> <span className="text-slate-300">{inv.recommendations}</span></div>
                  )}
                </div>
                <div className="flex items-center gap-4 text-xs text-slate-500 mt-3">
                  <span>Assigned: {new Date(inv.assigned_date).toLocaleDateString()}</span>
                  <span>•</span>
                  <span>Deadline: {inv.deadline}</span>
                </div>
              </div>
            ))
          )}
        </div>
      )}

      {/* Create Counseling Memo Modal */}
      {showMemoModal && (
        <div className="modal-overlay" data-testid="memo-modal">
          <div className="modal-content p-6 max-w-2xl">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-xl font-bold text-white">Create Counseling Memo</h2>
              <button onClick={() => setShowMemoModal(false)} className="text-slate-400 hover:text-white">
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <form onSubmit={handleCreateMemo} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-2">Personnel</label>
                  <select value={memoForm.user_id} onChange={(e) => setMemoForm({ ...memoForm, user_id: e.target.value })} required className="input-military">
                    <option value="">Select Personnel</option>
                    {personnel.filter(p => p.rank?.level < user.rank?.level).map((p) => (
                      <option key={p.id} value={p.id}>{p.full_name}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-2">Incident Date</label>
                  <input type="date" value={memoForm.incident_date} onChange={(e) => setMemoForm({ ...memoForm, incident_date: e.target.value })} required className="input-military" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-2">Category</label>
                  <select value={memoForm.category} onChange={(e) => setMemoForm({ ...memoForm, category: e.target.value })} className="input-military">
                    <option value="Performance">Performance</option>
                    <option value="Conduct">Conduct</option>
                    <option value="Attendance">Attendance</option>
                    <option value="Safety">Safety</option>
                    <option value="Policy Violation">Policy Violation</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-2">Severity</label>
                  <select value={memoForm.severity} onChange={(e) => setMemoForm({ ...memoForm, severity: e.target.value })} className="input-military">
                    <option value="Verbal">Verbal Warning</option>
                    <option value="Written">Written Warning</option>
                    <option value="Final Written">Final Written Warning</option>
                  </select>
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">Description</label>
                <textarea value={memoForm.description} onChange={(e) => setMemoForm({ ...memoForm, description: e.target.value })} required className="input-military h-24 resize-none" placeholder="Describe the incident..." />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">Expectations</label>
                <textarea value={memoForm.expectations} onChange={(e) => setMemoForm({ ...memoForm, expectations: e.target.value })} required className="input-military h-20 resize-none" placeholder="Expected behavior/improvement..." />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">Consequences</label>
                <textarea value={memoForm.consequences} onChange={(e) => setMemoForm({ ...memoForm, consequences: e.target.value })} required className="input-military h-20 resize-none" placeholder="Consequences if behavior continues..." />
              </div>
              <div className="flex justify-end gap-3 pt-4">
                <button type="button" onClick={() => setShowMemoModal(false)} className="btn-military">Cancel</button>
                <button type="submit" className="btn-gold">Create Memo</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Enforcement Action Modal */}
      {showEnforcementModal && (
        <div className="modal-overlay" data-testid="enforcement-modal">
          <div className="modal-content p-6">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-xl font-bold text-white">Create Enforcement Action</h2>
              <button onClick={() => setShowEnforcementModal(false)} className="text-slate-400 hover:text-white">
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <form onSubmit={handleCreateEnforcement} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">Personnel</label>
                <select value={enforcementForm.user_id} onChange={(e) => setEnforcementForm({ ...enforcementForm, user_id: e.target.value })} required className="input-military">
                  <option value="">Select Personnel</option>
                  {personnel.filter(p => p.rank?.level < user.rank?.level).map((p) => (
                    <option key={p.id} value={p.id}>{p.full_name}</option>
                  ))}
                </select>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-2">Action Type</label>
                  <select value={enforcementForm.action_type} onChange={(e) => setEnforcementForm({ ...enforcementForm, action_type: e.target.value })} className="input-military">
                    <option value="Warning">Warning</option>
                    <option value="Suspension">Suspension</option>
                    <option value="Demotion">Demotion</option>
                    <option value="Discharge">Discharge</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-2">Effective Date</label>
                  <input type="date" value={enforcementForm.effective_date} onChange={(e) => setEnforcementForm({ ...enforcementForm, effective_date: e.target.value })} required className="input-military" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-2">End Date (for Suspension)</label>
                  <input type="date" value={enforcementForm.end_date} onChange={(e) => setEnforcementForm({ ...enforcementForm, end_date: e.target.value })} className="input-military" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-2">Appeal Deadline</label>
                  <input type="date" value={enforcementForm.appeal_deadline} onChange={(e) => setEnforcementForm({ ...enforcementForm, appeal_deadline: e.target.value })} className="input-military" placeholder="Default: 14 days" />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">Reason</label>
                <textarea value={enforcementForm.reason} onChange={(e) => setEnforcementForm({ ...enforcementForm, reason: e.target.value })} required className="input-military h-24 resize-none" placeholder="Reason for enforcement action..." />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">Terms & Conditions</label>
                <textarea value={enforcementForm.terms_and_conditions} onChange={(e) => setEnforcementForm({ ...enforcementForm, terms_and_conditions: e.target.value })} className="input-military h-20 resize-none" placeholder="Terms and conditions (optional)..." />
              </div>
              <div className="flex justify-end gap-3 pt-4">
                <button type="button" onClick={() => setShowEnforcementModal(false)} className="btn-military">Cancel</button>
                <button type="submit" className="btn-gold">Create Action</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Ethics Violation Modal */}
      {showEthicsModal && (
        <div className="modal-overlay" data-testid="ethics-modal">
          <div className="modal-content p-6">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-xl font-bold text-white">Report Ethics Violation</h2>
              <button onClick={() => setShowEthicsModal(false)} className="text-slate-400 hover:text-white">
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <form onSubmit={handleReportEthics} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">Personnel</label>
                <select value={ethicsForm.user_id} onChange={(e) => setEthicsForm({ ...ethicsForm, user_id: e.target.value })} required className="input-military">
                  <option value="">Select Personnel</option>
                  {personnel.map((p) => (<option key={p.id} value={p.id}>{p.full_name}</option>))}
                </select>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-2">Violation Type</label>
                  <select value={ethicsForm.violation_type} onChange={(e) => setEthicsForm({ ...ethicsForm, violation_type: e.target.value })} className="input-military">
                    <option value="Policy Violation">Policy Violation</option>
                    <option value="Conflict of Interest">Conflict of Interest</option>
                    <option value="Harassment">Harassment</option>
                    <option value="Fraud">Fraud</option>
                    <option value="Misuse of Authority">Misuse of Authority</option>
                    <option value="Breach of Confidentiality">Breach of Confidentiality</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-2">Severity</label>
                  <select value={ethicsForm.severity} onChange={(e) => setEthicsForm({ ...ethicsForm, severity: e.target.value })} className="input-military">
                    <option value="Minor">Minor</option>
                    <option value="Major">Major</option>
                    <option value="Critical">Critical</option>
                  </select>
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">Incident Date</label>
                <input type="date" value={ethicsForm.incident_date} onChange={(e) => setEthicsForm({ ...ethicsForm, incident_date: e.target.value })} required className="input-military" />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">Description</label>
                <textarea value={ethicsForm.description} onChange={(e) => setEthicsForm({ ...ethicsForm, description: e.target.value })} required className="input-military h-24 resize-none" placeholder="Describe the ethics violation..." />
              </div>
              <div className="flex justify-end gap-3 pt-4">
                <button type="button" onClick={() => setShowEthicsModal(false)} className="btn-military">Cancel</button>
                <button type="submit" className="btn-gold">Report Violation</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* File Appeal Modal */}
      {showAppealModal && selectedAction && (
        <div className="modal-overlay" data-testid="appeal-modal">
          <div className="modal-content p-6">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-xl font-bold text-white">File Appeal</h2>
              <button onClick={() => { setShowAppealModal(false); setSelectedAction(null); }} className="text-slate-400 hover:text-white">
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <div className="bg-slate-800/50 rounded-lg p-4 mb-6">
              <p className="text-slate-400 text-sm">Appealing: <span className="text-red-400 font-mono">{selectedAction.action_number}</span></p>
              <p className="text-white">{selectedAction.action_type} - {selectedAction.reason?.substring(0, 100)}...</p>
            </div>
            <form onSubmit={handleFileAppeal} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">Grounds for Appeal</label>
                <select value={appealForm.grounds_for_appeal} onChange={(e) => setAppealForm({ ...appealForm, grounds_for_appeal: e.target.value })} required className="input-military">
                  <option value="">Select Grounds</option>
                  <option value="Procedural irregularities">Procedural Irregularities</option>
                  <option value="New evidence">New Evidence</option>
                  <option value="Mitigating circumstances">Mitigating Circumstances</option>
                  <option value="Factual errors">Factual Errors</option>
                  <option value="Disproportionate punishment">Disproportionate Punishment</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">Supporting Statement</label>
                <textarea value={appealForm.supporting_statement} onChange={(e) => setAppealForm({ ...appealForm, supporting_statement: e.target.value })} required className="input-military h-32 resize-none" placeholder="Provide detailed justification for your appeal..." />
              </div>
              <div className="flex justify-end gap-3 pt-4">
                <button type="button" onClick={() => { setShowAppealModal(false); setSelectedAction(null); }} className="btn-military">Cancel</button>
                <button type="submit" className="btn-gold">Submit Appeal</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Decide Appeal Modal */}
      {showDecideAppealModal && selectedAppeal && (
        <div className="modal-overlay" data-testid="decide-appeal-modal">
          <div className="modal-content p-6">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-xl font-bold text-white">Decide Appeal</h2>
              <button onClick={() => { setShowDecideAppealModal(false); setSelectedAppeal(null); }} className="text-slate-400 hover:text-white">
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <div className="bg-slate-800/50 rounded-lg p-4 mb-6">
              <p className="text-slate-400 text-sm mb-2">Appeal for: <span className="text-amber-400 font-mono">{selectedAppeal.action_number}</span></p>
              <p className="text-white font-medium">Appellant: {selectedAppeal.appellant_name}</p>
              <p className="text-slate-300 text-sm mt-2">{selectedAppeal.supporting_statement}</p>
            </div>
            <form onSubmit={handleDecideAppeal} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">Decision</label>
                <select value={decisionForm.decision} onChange={(e) => setDecisionForm({ ...decisionForm, decision: e.target.value })} className="input-military">
                  <option value="Upheld">Upheld - Original action stands</option>
                  <option value="Modified">Modified - Reduce/change action</option>
                  <option value="Overturned">Overturned - Void original action</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">Decision Rationale</label>
                <textarea value={decisionForm.decision_rationale} onChange={(e) => setDecisionForm({ ...decisionForm, decision_rationale: e.target.value })} required className="input-military h-24 resize-none" placeholder="Explain the reasoning for this decision..." />
              </div>
              {decisionForm.decision === 'Modified' && (
                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-2">Modified Terms</label>
                  <textarea value={decisionForm.modified_terms} onChange={(e) => setDecisionForm({ ...decisionForm, modified_terms: e.target.value })} className="input-military h-20 resize-none" placeholder="Specify the modified terms..." />
                </div>
              )}
              <div className="flex justify-end gap-3 pt-4">
                <button type="button" onClick={() => { setShowDecideAppealModal(false); setSelectedAppeal(null); }} className="btn-military">Cancel</button>
                <button type="submit" className="btn-gold">Submit Decision</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Assign Investigation Modal */}
      {showAssignInvestigationModal && selectedViolation && (
        <div className="modal-overlay" data-testid="assign-investigation-modal">
          <div className="modal-content p-6">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-xl font-bold text-white">Assign Investigation</h2>
              <button onClick={() => { setShowAssignInvestigationModal(false); setSelectedViolation(null); }} className="text-slate-400 hover:text-white">
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <div className="bg-slate-800/50 rounded-lg p-4 mb-6">
              <p className="text-slate-400 text-sm mb-2">Violation: <span className="text-purple-400 font-mono">{selectedViolation.violation_number}</span></p>
              <p className="text-white">{selectedViolation.violation_type} - {selectedViolation.severity}</p>
              <p className="text-slate-300 text-sm mt-2">{selectedViolation.description}</p>
            </div>
            <form onSubmit={handleAssignInvestigation} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">Investigator (Warrant+ rank)</label>
                <select value={investigationForm.investigator_id} onChange={(e) => setInvestigationForm({ ...investigationForm, investigator_id: e.target.value })} required className="input-military">
                  <option value="">Select Investigator</option>
                  {personnel.filter(p => p.rank?.level >= 3).map((p) => (
                    <option key={p.id} value={p.id}>{p.full_name} ({p.rank?.name})</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">Investigation Scope</label>
                <textarea value={investigationForm.investigation_scope} onChange={(e) => setInvestigationForm({ ...investigationForm, investigation_scope: e.target.value })} required className="input-military h-24 resize-none" placeholder="Define the scope and objectives of the investigation..." />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">Deadline</label>
                <input type="date" value={investigationForm.deadline} onChange={(e) => setInvestigationForm({ ...investigationForm, deadline: e.target.value })} required className="input-military" />
              </div>
              <div className="flex justify-end gap-3 pt-4">
                <button type="button" onClick={() => { setShowAssignInvestigationModal(false); setSelectedViolation(null); }} className="btn-military">Cancel</button>
                <button type="submit" className="btn-gold">Assign Investigation</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

export default Disciplinary;
